
import { Target, Sparkles, Building2, Shield, Users, Zap } from 'lucide-react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export default function About() {
  const whyReveal = useScrollReveal();
  const missionReveal = useScrollReveal();
  const valuesReveal = useScrollReveal();

  return (
    <div className="bg-[var(--t-bg)] text-[var(--t-text)] selection:bg-indigo-500/30 pb-40 overflow-visible">
      {/* Hero Section */}
      <section className="pt-40 pb-24 text-center px-6 relative overflow-visible w-full">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-indigo-500/10 blur-[120px] rounded-full pointer-events-none opacity-20" />
        <div className="relative z-10 max-w-5xl mx-auto overflow-visible w-full">
          <h1 
            className="text-4xl md:text-5xl font-black mb-8 leading-[0.85] tracking-tighter italic uppercase px-6"
            style={{ overflow: 'visible', whiteSpace: 'nowrap' }}
          >
            The <span className="inline-block pr-6 text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">Blueprint</span>
          </h1>
          <p className="text-xl md:text-2xl text-[var(--t-text-muted)] max-w-3xl mx-auto leading-relaxed font-medium">
            We're not just another CRM. We're building the infrastructure for the next generation of real estate empires.
          </p>
        </div>
      </section>

      {/* Why We Built This */}
      <section className="max-w-7xl mx-auto px-6 mb-40" ref={whyReveal.elementRef}>
        <div className={`grid md:grid-cols-2 gap-20 items-center transition-all duration-1000 ${whyReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="space-y-8">
            <h2 className="text-5xl font-black italic uppercase tracking-tight">Origin <span className="text-indigo-500">Story</span></h2>
            <div className="space-y-6 text-lg text-[var(--t-text-muted)] font-medium leading-relaxed">
              <p>
                In 2023, we saw a massive gap in the market. Legacy CRMs were bloated, slow, and most importantly, they held your data hostage. 
              </p>
              <p>
                WholeScale OS started as an internal tool for high-volume wholesalers who needed to triage thousands of leads without losing their minds. We needed something mission-critical.
              </p>
              <p>
                Today, WholeScale OS has evolved into a complete infrastructure stack for elite real estate professionals. Our philosophy remains unchanged: <span className="text-white">Speed, Sovereignty, and Scale.</span>
              </p>
            </div>
          </div>
          <div className="p-1.5 rounded-[3rem] bg-gradient-to-br from-indigo-500/20 to-transparent border border-white/10 overflow-hidden shadow-2xl">
             <div className="bg-[var(--t-surface)] rounded-[2.8rem] p-16 aspect-[4/3] flex items-center justify-center relative overflow-hidden group">
                <div className="absolute inset-0 bg-indigo-500/5 blur-3xl group-hover:scale-150 transition-transform duration-1000" />
                <Building2 size={160} className="text-indigo-400/10 group-hover:text-indigo-400/20 transition-all duration-700 relative z-10" />
             </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 mb-40" ref={missionReveal.elementRef}>
        <div className={`p-12 rounded-[3rem] bg-[var(--t-surface)] border border-[var(--t-border)] space-y-8 hover-glow transition-all duration-1000 ${missionReveal.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
           <div className="w-20 h-20 rounded-3xl bg-indigo-500/10 flex items-center justify-center text-indigo-400 shadow-inner"><Target size={40} /></div>
           <h3 className="text-3xl font-black italic uppercase tracking-tight">Our Mission</h3>
           <p className="text-xl text-[var(--t-text-muted)] leading-relaxed font-medium">
             Empower real estate professionals with state-of-the-art AI infrastructure to eliminate manual triage and maximize closing velocity.
           </p>
        </div>
        <div 
          className={`p-12 rounded-[3rem] bg-[var(--t-surface)] border border-[var(--t-border)] space-y-8 hover-glow transition-all duration-1000 ${missionReveal.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
        >
           <div className="w-20 h-20 rounded-3xl bg-purple-500/10 flex items-center justify-center text-purple-400 shadow-inner"><Sparkles size={40} /></div>
           <h3 className="text-3xl font-black italic uppercase tracking-tight">Our Vision</h3>
           <p className="text-xl text-[var(--t-text-muted)] leading-relaxed font-medium">
             To become the definitive operating system for real estate, where every transaction is powered by sovereign data and intelligent automation.
           </p>
        </div>
      </section>

      {/* Values */}
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-12 mb-40" ref={valuesReveal.elementRef}>
        {[
          { icon: Zap, title: 'Extreme Speed', desc: 'Milliseconds matter. Our triage engine is built for the fastest response times in the industry.', color: 'text-indigo-400', bg: 'bg-indigo-500/10' },
          { icon: Shield, title: 'Data Sovereignty', desc: "Your data is yours. We provide the infrastructure, you provide the empire.", color: 'text-purple-400', bg: 'bg-purple-500/10' },
          { icon: Users, title: 'Elite Community', desc: "We're a small team of engineers and operators passionate about real estate tech.", color: 'text-pink-400', bg: 'bg-pink-500/10' }
        ].map((value, i) => (
          <div 
            key={i} 
            className={`p-10 rounded-[2.5rem] bg-[var(--t-surface)] border border-[var(--t-border)] text-center group hover:border-indigo-500/30 transition-all duration-1000 ${valuesReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className={`w-16 h-16 rounded-2xl ${value.bg} ${value.color} flex items-center justify-center mx-auto mb-8 group-hover:scale-110 transition-transform shadow-inner`}>
              <value.icon size={32} />
            </div>
            <h3 className="text-2xl font-black uppercase tracking-tight mb-4 italic">{value.title}</h3>
            <p className="text-[var(--t-text-muted)] leading-relaxed text-base font-medium">{value.desc}</p>
          </div>
        ))}
      </div>

      <section className="max-w-5xl mx-auto px-6 bg-gradient-to-br from-indigo-600 to-purple-800 rounded-[4rem] border border-white/10 p-16 md:p-24 text-center shadow-2xl relative overflow-hidden group">
         <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
         <h2 className="text-4xl md:text-5xl font-black italic mb-10 leading-[1.1] text-white uppercase tracking-tight relative z-10">
           "The industry is changing. The winners will be those who embrace automation without losing the human touch."
         </h2>
         <p className="text-white/60 font-black uppercase tracking-[0.4em] text-xs relative z-10">— The WholeScale OS Core —</p>
      </section>
    </div>
  );
}
